import React, { useState, useEffect } from 'react';
import { Home, CreditCard, Users, ArrowLeft, ArrowRight, Check, Search, X, UserPlus } from 'lucide-react';
import { authAPI } from '../services/api';
import '../styles/Modal.css';
import '../styles/Wizard.css';

function CreateHouseholdWizard({ isOpen, onClose, onComplete, currentUser }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    householdName: '',
    accounts: [],
    invitations: [] // Will store user objects: { id, first_name, last_name, email }
  });

  // For user search
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const steps = [
    { id: 1, title: 'Basic Information', icon: Home },
    { id: 2, title: 'Payment Accounts', icon: CreditCard },
    { id: 3, title: 'Invite Members', icon: Users },
    { id: 4, title: 'Review', icon: Check }
  ];

  // Load available users when reaching step 3
  useEffect(() => {
    if (isOpen && currentStep === 3) {
      loadAvailableUsers();
    }
  }, [isOpen, currentStep]);

  const loadAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await authAPI.getAvailableUsers();
      setAvailableUsers(response.data);
    } catch (err) {
      console.error('Failed to load available users:', err);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    
    try {
      await onComplete(formData);
    } catch (error) {
      console.error('Failed to create household:', error);
      setError(error.response?.data?.error || error.message || 'Failed to create household');
      setSubmitting(false);
    }
  };

  // Account management
  const addAccount = () => {
    setFormData({
      ...formData,
      accounts: [...formData.accounts, {
        id: Date.now(),
        accountName: '',
        accountType: '',
        lastFour: '',
        institutionName: ''
      }]
    });
  };

  const removeAccount = (id) => {
    setFormData({
      ...formData,
      accounts: formData.accounts.filter(acc => acc.id !== id)
    });
  };

  const updateAccount = (id, field, value) => {
    setFormData({
      ...formData,
      accounts: formData.accounts.map(acc =>
        acc.id === id ? { ...acc, [field]: value } : acc
      )
    });
  };

  // Invitation management
  const addInvitation = (user) => {
    // Don't add if already invited
    if (formData.invitations.some(inv => inv.id === user.id)) {
      return;
    }
    setFormData({
      ...formData,
      invitations: [...formData.invitations, user]
    });
    setSearchTerm('');
  };

  const removeInvitation = (userId) => {
    setFormData({
      ...formData,
      invitations: formData.invitations.filter(inv => inv.id !== userId)
    });
  };

  // Filter available users based on search, already invited, and exclude current user
  const filteredUsers = availableUsers.filter(user => {
    // Exclude current user
    if (currentUser && user.id === currentUser.id) return false;
    
    const isAlreadyInvited = formData.invitations.some(inv => inv.id === user.id);
    if (isAlreadyInvited) return false;
    
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      user.first_name.toLowerCase().includes(search) ||
      user.last_name.toLowerCase().includes(search) ||
      user.email.toLowerCase().includes(search)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="wizard-container">
        {/* Header */}
        <div className="wizard-header">
          <h2>Create Your Household</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Progress Steps */}
        <div className="wizard-steps">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="wizard-step-wrapper">
                <div className={`wizard-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                  <div className="step-icon-circle">
                    {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                  </div>
                  <div className="step-label">
                    <span>{step.title}</span>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`step-connector ${isCompleted ? 'completed' : ''}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="wizard-body">
          {error && (
            <div className="form-error" style={{ marginBottom: 'var(--spacing-lg)' }}>
              {error}
            </div>
          )}

          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="wizard-step-content">
              <h3>Basic Information</h3>
              <p className="step-description">Let's start with some basic details about your household</p>

              <div className="form-group">
                <label htmlFor="householdName">Household Name</label>
                <div className="input-with-indicator">
                  <input
                    type="text"
                    id="householdName"
                    value={formData.householdName}
                    onChange={(e) => handleChange('householdName', e.target.value)}
                    placeholder="e.g., The Smith Family"
                    required
                    autoFocus
                  />
                  <span className="required-indicator">*</span>
                </div>
                <small className="form-hint">This will be visible to all household members</small>
              </div>
            </div>
          )}

          {/* Step 2: Payment Accounts */}
          {currentStep === 2 && (
            <div className="wizard-step-content">
              <h3>Payment Accounts</h3>
              <p className="step-description">Add payment accounts for your household</p>

              {formData.accounts.length === 0 ? (
                <div className="empty-state-small">
                  <CreditCard size={48} />
                  <p>No payment accounts added yet</p>
                  <button className="btn-secondary" onClick={addAccount}>
                    <CreditCard size={18} />
                    Add Payment Account
                  </button>
                </div>
              ) : (
                <>
                  {formData.accounts.map((account) => (
                    <div key={account.id} className="account-form-card">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Account Type *</label>
                          <select
                            value={account.accountType}
                            onChange={(e) => updateAccount(account.id, 'accountType', e.target.value)}
                            required
                          >
                            <option value="">Select type...</option>
                            <option value="checking">Checking</option>
                            <option value="savings">Savings</option>
                            <option value="credit_card">Credit Card</option>
                            <option value="cash">Cash</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Account Name *</label>
                          <input
                            type="text"
                            value={account.accountName}
                            onChange={(e) => updateAccount(account.id, 'accountName', e.target.value)}
                            placeholder="e.g., Chase Checking"
                            required
                          />
                        </div>
                      </div>

                      {account.accountType && account.accountType !== 'cash' && (
                        <div className="form-row">
                          <div className="form-group">
                            <label>Institution</label>
                            <input
                              type="text"
                              value={account.institutionName}
                              onChange={(e) => updateAccount(account.id, 'institutionName', e.target.value)}
                              placeholder="e.g., Chase Bank"
                            />
                          </div>

                          <div className="form-group">
                            <label>Last 4 Digits</label>
                            <input
                              type="text"
                              value={account.lastFour}
                              onChange={(e) => updateAccount(account.id, 'lastFour', e.target.value)}
                              maxLength={4}
                              placeholder="1234"
                            />
                          </div>
                        </div>
                      )}

                      <button
                        className="btn-icon danger"
                        onClick={() => removeAccount(account.id)}
                        style={{ marginTop: 'var(--spacing-sm)' }}
                      >
                        Remove Account
                      </button>
                    </div>
                  ))}

                  <button className="btn-secondary" onClick={addAccount} style={{ marginTop: 'var(--spacing-md)' }}>
                    <CreditCard size={18} />
                    Add Another Account
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step 3: Invite Members */}
          {currentStep === 3 && (
            <div className="wizard-step-content">
              <h3>Invite Members</h3>
              <p className="step-description">Invite registered users to join your household</p>

              {/* Selected invitations */}
              {formData.invitations.length > 0 && (
                <div className="selected-users" style={{ marginBottom: 'var(--spacing-lg)' }}>
                  <label style={{ display: 'block', marginBottom: 'var(--spacing-sm)', fontWeight: 500 }}>
                    Selected Members ({formData.invitations.length})
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-sm)' }}>
                    {formData.invitations.map((user) => (
                      <div 
                        key={user.id} 
                        className="user-chip"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-sm)',
                          padding: 'var(--spacing-xs) var(--spacing-sm)',
                          background: 'var(--primary-color)',
                          color: 'white',
                          borderRadius: 'var(--border-radius-sm)',
                          fontSize: '0.9rem'
                        }}
                      >
                        <span>{user.first_name} {user.last_name}</span>
                        <button
                          onClick={() => removeInvitation(user.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            padding: '2px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search for users */}
              <div className="form-group">
                <label>Search Users</label>
                <div style={{ position: 'relative' }}>
                  <Search 
                    size={18} 
                    style={{ 
                      position: 'absolute', 
                      left: '12px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: 'var(--text-secondary)'
                    }} 
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name or email..."
                    style={{ paddingLeft: '40px' }}
                  />
                </div>
              </div>

              {/* Available users list */}
              <div 
                className="users-list"
                style={{
                  maxHeight: '250px',
                  overflowY: 'auto',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius-sm)',
                  marginTop: 'var(--spacing-sm)'
                }}
              >
                {loadingUsers ? (
                  <div style={{ padding: 'var(--spacing-lg)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Loading users...
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div style={{ padding: 'var(--spacing-lg)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    {availableUsers.length === 0 
                      ? 'No users available to invite. Users must be registered and not already in a household.'
                      : searchTerm 
                        ? 'No users match your search'
                        : 'All available users have been selected'
                    }
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className="user-item"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 'var(--spacing-md)',
                        borderBottom: '1px solid var(--border-color)',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onClick={() => addInvitation(user)}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-color)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div>
                        <div style={{ fontWeight: 500 }}>{user.first_name} {user.last_name}</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{user.email}</div>
                      </div>
                      <UserPlus size={18} style={{ color: 'var(--primary-color)' }} />
                    </div>
                  ))
                )}
              </div>

              <div className="info-box" style={{ marginTop: 'var(--spacing-lg)' }}>
                <h4>ℹ️ About Invitations</h4>
                <p>
                  Selected users will receive an invitation notification after your household is created.
                  They can accept or decline from their account. Members will join with standard permissions.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="wizard-step-content">
              <h3>Review & Create</h3>
              <p className="step-description">Review your household details before creating</p>

              <div className="review-section">
                <h4>Basic Information</h4>
                <div className="review-item">
                  <span>Household Name:</span>
                  <strong>{formData.householdName}</strong>
                </div>
              </div>

              <div className="review-section">
                <h4>Payment Accounts</h4>
                {formData.accounts.length === 0 ? (
                  <p className="text-secondary">None added</p>
                ) : (
                  formData.accounts.map((account) => (
                    <div key={account.id} className="review-item">
                      <span>{account.accountName}</span>
                      <strong>{account.accountType}</strong>
                    </div>
                  ))
                )}
              </div>

              <div className="review-section">
                <h4>Invitations</h4>
                {formData.invitations.length === 0 ? (
                  <p className="text-secondary">None added</p>
                ) : (
                  formData.invitations.map((user) => (
                    <div key={user.id} className="review-item">
                      <span>{user.first_name} {user.last_name}</span>
                      <strong style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{user.email}</strong>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="wizard-footer">
          <button
            className="btn-secondary"
            onClick={currentStep === 1 ? onClose : handleBack}
          >
            {currentStep === 1 ? (
              'Cancel'
            ) : (
              <>
                <ArrowLeft size={18} />
                Back
              </>
            )}
          </button>

          {currentStep < steps.length ? (
            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={currentStep === 1 && !formData.householdName.trim()}
            >
              Next
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                'Creating...'
              ) : (
                <>
                  <Check size={18} />
                  Create Household
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateHouseholdWizard;
